import { API_URLS } from "@/config/urls";
import { api } from "@/lib/axios";
import type { TopicPayload } from "@/features/study-group-stream/types/postChannelMove";
import type { PaginatedResponse, PostResponse } from "@/lib/humhub-api/types";

type ChannelQueryOptions = {
  channelId?: number | string;
  channelKey?: string;
};

export type RequestParams = Record<string, string | number>;

const normalizeChannelId = (channelId?: number | string) => {
  if (typeof channelId === "number") {
    return Number.isFinite(channelId) ? channelId : undefined;
  }
  if (typeof channelId === "string") {
    const trimmed = channelId.trim();
    return trimmed ? trimmed : undefined;
  }
  return undefined;
};

const normalizeChannelKey = (channelKey?: string) => {
  if (typeof channelKey !== "string") return undefined;
  const trimmed = channelKey.trim();
  return trimmed ? trimmed : undefined;
};

export const buildChannelQueryParams = ({
  channelId,
  channelKey,
}: ChannelQueryOptions): RequestParams => {
  const normalizedChannelId = normalizeChannelId(channelId);
  if (normalizedChannelId !== undefined) {
    return { channel_id: normalizedChannelId };
  }

  const normalizedChannelKey = normalizeChannelKey(channelKey);
  if (normalizedChannelKey) {
    return { channel_key: normalizedChannelKey };
  }

  return {};
};

export const appendTopicsToFormData = (
  formData: FormData,
  topics?: TopicPayload[],
) => {
  if (!topics?.length) return;
  topics
    .filter((topic) => topic.id !== undefined && topic.id !== null)
    .forEach((topic, index) => {
      formData.append(`data[content][topics][${index}][id]`, String(topic.id));
      if (topic.topic_kind) {
        formData.append(
          `data[content][topics][${index}][topic_kind]`,
          topic.topic_kind,
        );
      }
    });
};

export const fetchAllPinnedPostPages = async (
  containerId: number,
  params: RequestParams,
  firstPage: PaginatedResponse<PostResponse>,
): Promise<PaginatedResponse<PostResponse>> => {
  const totalPages = Math.max(firstPage.pages ?? 1, 1);
  if (totalPages <= 1) {
    return firstPage;
  }

  const restResults: PostResponse[] = [];
  for (let page = 2; page <= totalPages; page += 1) {
    const restResponse = await api.get<PaginatedResponse<PostResponse>>(
      `${API_URLS.POST}/container/${containerId}/pinned`,
      {
        params: {
          ...params,
          page,
        },
      },
    );
    restResults.push(...(restResponse.data.results ?? []));
  }

  const mergedResults = [...(firstPage.results ?? []), ...restResults];
  const dedupedResults = Array.from(
    new Map(mergedResults.map((item) => [item.id, item])).values(),
  );

  return {
    ...firstPage,
    page: 1,
    pages: 1,
    total: dedupedResults.length,
    results: dedupedResults,
  };
};
